import React from 'react';
import { Box, Typography } from '@drone-js/core';
import { OfferCardFooterProps, OfferCardFooterType } from 'component/page/detail/PackageOptions/ExpandedOffer/OfferCardBookButtonSection/OfferCardFooter.selectors';
import { Price } from 'component/common/Price/Price';
import { isPricePeekPromocode, mapDealsBadgesFromPricingSummary, mapPromocodeBadgesFromPricingSummary } from 'gql/service/graph/activityService';
import ActivityCardBadge from 'component/common/Badge/ActivityCardBadge';
import { ActivityBadgeCategory } from 'component/common/Badge/BadgeService.types';
import Feature from 'component/common/Experiment/Feature';
import { FeatureName } from 'core/constant/FeatureName';

const getPricingSummary = (props: OfferCardFooterProps) => {
    return props.finalOfferOption?.pricingSummary;
};

export const OfferCardFooterPriceContainer: React.FC<OfferCardFooterProps> = (props) => {
    const { isLoading, selectedOffer, isSearchCriteriaUpdated } = props;
    const { cms, behavior } = props.config;
    const activityId = selectedOffer.offer?.activityId;
    const pricingSummary = getPricingSummary(props);
    const shouldShowPrice = !isSearchCriteriaUpdated && !isLoading && pricingSummary && !props.hasOnlyDisabledOptions;
    const isBoxedFooterType = props.footerType === OfferCardFooterType.Boxed;
    const isSummaryCardType = props.footerType === OfferCardFooterType.SummaryCard;
    const summaryText = isBoxedFooterType ? null : isSummaryCardType ? cms[54650] : cms[behavior.availability.bookingOptions.summaryCms];
    const promocodeBadges = mapPromocodeBadgesFromPricingSummary(pricingSummary, props.config.experiments);
    const dealsBadges = mapDealsBadgesFromPricingSummary(pricingSummary);
    const isPricePeek = isPricePeekPromocode(pricingSummary);

    return (
        <>
            <Feature.Off name={FeatureName.VIPBadgeDetail}>
                <Box
                    display="flex"
                    justifyContent="space-between"
                    alignContent="center"
                    height="100%"
                    alignItems={isSummaryCardType ? 'start' : 'center'}
                    data-testid="booking-options-price-container"
                >
                    {(!isSummaryCardType || shouldShowPrice) && (
                        <Typography tag="p" variant={isSummaryCardType ? 'heading-default' : 'body-default-strong'} pt={isSummaryCardType ? '4' : undefined}>
                            {summaryText}
                        </Typography>
                    )}
                    {shouldShowPrice && (
                        <Box textAlign="end">
                            <Price
                                activityId={activityId}
                                pricingSummary={pricingSummary}
                                isShowPrefix={false}
                                priceDisplay={behavior.common.price.displayType}
                                priceJustifyContent="flex-end"
                                finalPriceSize="l"
                                priceDirection={isSummaryCardType ? 'column-reverse' : undefined}
                                priceAlignItems={isSummaryCardType ? 'flex-end' : undefined}
                            />
                            {props.showInclusiveTax && (
                                <Typography variant="caption-default" textColor="base.subtle">
                                    {cms[319651]}
                                </Typography>
                            )}
                        </Box>
                    )}
                </Box>
                {shouldShowPrice && promocodeBadges.length > 0 && (isPricePeek || !isSummaryCardType) && (
                    <Box display="flex" pt="8" alignSelf="flex-end" data-testid="activity-promocodes-badges-container">
                        <ActivityCardBadge badges={promocodeBadges} activityId={activityId || 0} badgeCategory={ActivityBadgeCategory.PROMOCODE} isBVariant={true} />
                    </Box>
                )}
            </Feature.Off>
            <Feature.On name={FeatureName.VIPBadgeDetail}>
                {shouldShowPrice && (
                    <Box display="flex" pb="4" alignSelf="flex-end" data-testid="activity-deals-badges-container">
                        <ActivityCardBadge badges={dealsBadges} activityId={activityId || 0} badgeCategory={ActivityBadgeCategory.DEAL} isBVariant={true} />
                    </Box>
                )}
                {shouldShowPrice && promocodeBadges.length > 0 && (isPricePeek || !isSummaryCardType) && (
                    <Box display="flex" pb="4" alignSelf="flex-end" data-testid="activity-promocodes-badges-container">
                        <ActivityCardBadge badges={promocodeBadges} activityId={activityId || 0} badgeCategory={ActivityBadgeCategory.PROMOCODE} isBVariant={true} />
                    </Box>
                )}
                <Box
                    display="flex"
                    justifyContent="space-between"
                    alignContent="center"
                    height="100%"
                    alignItems={isSummaryCardType ? 'start' : 'center'}
                    data-testid="booking-options-price-container"
                >
                    {(!isSummaryCardType || shouldShowPrice) && (
                        <Box alignSelf="flex-end">
                            <Typography tag="p" variant={isSummaryCardType ? 'heading-default' : 'body-default-strong'} pt={isSummaryCardType ? '4' : undefined}>
                                {summaryText}
                            </Typography>
                        </Box>
                    )}
                    {shouldShowPrice && (
                        <Box textAlign="end">
                            <Price
                                activityId={activityId}
                                pricingSummary={pricingSummary}
                                isShowPrefix={false}
                                priceDisplay={behavior.common.price.displayType}
                                priceJustifyContent="flex-end"
                                finalPriceSize="l"
                                priceDirection={isSummaryCardType ? 'column' : undefined}
                                priceAlignItems={isSummaryCardType ? 'flex-end' : undefined}
                            />
                            {props.showInclusiveTax && (
                                <Typography variant="caption-default" textColor="base.subtle">
                                    {cms[319651]}
                                </Typography>
                            )}
                        </Box>
                    )}
                </Box>
            </Feature.On>
        </>
    );
};
OfferCardFooterPriceContainer.displayName = 'OfferCardFooterPriceContainer';
